import React, { useEffect, useMemo, useState } from "react";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import { useParams } from "react-router-dom";
import loginBg from "../../image/loginbackground.png";

const WEEKEND_DAYS = [0]; // Sunday

// 🔹 CONFIG
const BORDER_COLOR_CLASS = "border-gray-300";
const SHOW_TODAY_HIGHLIGHT = true;
const TODAY_BG_CLASS = "bg-blue-100";
const DATE_FONT_CLASS = "text-sm sm:text-base";

// 🔹 HEADER COLORS
const HEADER_TEXT_CLASS = "text-sky-600";
const HEADER_BTN_CLASS = "bg-sky-500 hover:bg-sky-600 text-white";

// 🔹 LEFT CIRCLE COLORS
const HOLIDAY_COLOR = "bg-blue-500";
const APPROVED_LEAVE_COLOR = "bg-sky-400";
const REJECTED_LEAVE_COLOR = "bg-yellow-200";

const EmployeeCalendar = (props) => {
  const params = useParams();

  const [attendanceDetailsList, setAttendanceDetailsList] = useState([]);
  const [leaveDetailsList, setLeaveDetailsList] = useState([]);
  const [holidayList, setHolidayList] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // ================= ATTENDANCE =================
  const attendanceDetails = async () => {
    try {
      const attendance = await fetchQuery(
        `SELECT * FROM attendance WHERE employeeId=${params.id}`
      );
      setAttendanceDetailsList(attendance || []);
    } catch (err) {
      console.error("Attendance error", err);
    }
  };

  // ================= LEAVE =================
  const fetchLeaveDetails = async () => {
    try {
      const leaves = await fetchQuery(`
        SELECT fromDate, toDate,
               approveLevel2,
               approveManagerStatus,
               approveLevel1
        FROM leaveManagement
        WHERE employeeId = ${params.id}
          AND (
            approveLevel2 = 2
            OR approveManagerStatus = 3
            OR approveLevel1 = 3
            OR approveLevel2 = 3
          )
      `);
      setLeaveDetailsList(leaves || []);
    } catch (err) {
      console.error("Leave error", err);
    }
  };

  // ================= HOLIDAY =================
  const fetchHolidays = async () => {
    try {
      const holidays = await fetchQuery(`
        SELECT date, holidayName
        FROM holidayRegister
        WHERE status = 1
      `);
      setHolidayList(holidays || []);
    } catch (err) {
      console.error("Holiday error", err);
    }
  };

  useEffect(() => {
    attendanceDetails();
    fetchLeaveDetails();
    fetchHolidays();
  }, [params.id]);

  // ================= ATTENDANCE DATE SET (CHECKIN + CHECKOUT REQUIRED) =================
  const attendanceDateSet = useMemo(() => {
    return new Set(
      attendanceDetailsList
        .filter((a) => {
          if (!a.checkinDatetime || !a.checkoutDatetime) return false;

          const checkIn = new Date(a.checkinDatetime);
          const checkOut = new Date(a.checkoutDatetime);

        const diffInMs = checkOut - checkIn;
        const diffInHours = diffInMs / (1000 * 60 * 60);

        return diffInHours >= 4; // ✅ minimum 4 hours
        })
        .map((a) => {
          const date = new Date(a.checkinDatetime);
          date.setHours(0, 0, 0, 0);
          return date.toDateString();
        })
    );
  }, [attendanceDetailsList]);


  // ================= HOLIDAY DATE SET =================
  const holidayDateSet = useMemo(() => {
    return new Set(holidayList.map((h) => new Date(h.date).toDateString()));
  }, [holidayList]);




  // ================= LEAVE DATE MAP =================
  const leaveDateMap = useMemo(() => {
    const map = new Map();

    leaveDetailsList.forEach((leave) => {
      const start = new Date(leave.fromDate);
      const end = new Date(leave.toDate);

      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = d.toDateString();

        if (leave.approveLevel2 === 2) {
          map.set(key, APPROVED_LEAVE_COLOR);
        } else if (
          leave.approveManagerStatus === 3 ||
          leave.approveLevel1 === 3 ||
          leave.approveLevel2 === 3
        ) {
          map.set(key, REJECTED_LEAVE_COLOR);
        }
      }
    });

    return map;
  }, [leaveDetailsList]);

  // ================= CALENDAR BUILD =================
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  const calendarDays = [];

  // Empty slots
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push(null);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dateObj = new Date(year, month, day);
    dateObj.setHours(0, 0, 0, 0);

    const dayOfWeek = dateObj.getDay();
    const dateKey = dateObj.toDateString();
    const isHoliday = holidayDateSet.has(dateKey);
    const isFutureDate = dateObj > today;
    const isToday =
      dateObj.getTime() === today.getTime() && SHOW_TODAY_HIGHLIGHT;

    // 🔹 Attendance (RIGHT)
    let statusColor = "bg-[rgb(255,128,128)]"; // Absent
    if (WEEKEND_DAYS.includes(dayOfWeek)) statusColor = "bg-black";
    if (attendanceDateSet.has(dateKey)) statusColor = "bg-green-500";

    // 🔹 LEFT (Holiday > Leave)
    let leftColor = null;
    if (holidayDateSet.has(dateKey)) {
      leftColor = HOLIDAY_COLOR;
    } else if (leaveDateMap.has(dateKey)) {
      leftColor = leaveDateMap.get(dateKey);
    }

    calendarDays.push({
      day,
      statusColor,
      leftColor,
      isFutureDate,
      isToday,
      isHoliday, 
    });
  }

  // ================= MONTH NAV =================
  const goToPrevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear((y) => y - 1);
    } else setMonth((m) => m - 1);
  };

  const goToNextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear((y) => y + 1);
    } else setMonth((m) => m + 1);
  };

  // ================= UI =================
  return (
    <>
      {/* Full page bg */}
      <div style={{ position: "fixed", inset: 0, zIndex: 0, backgroundImage: `url(${loginBg})`, backgroundSize: "cover", backgroundPosition: "center" }} />

      <div
        className={`relative z-10 max-w-3xl mx-auto p-[20px] border-2 ${BORDER_COLOR_CLASS} rounded-3xl`}
        style={{ backgroundColor: "rgba(255,255,255,0.92)" }}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-base font-semibold">Employee Attendance</h2>

          <div className="flex items-center gap-2">
            <button onClick={goToPrevMonth} className={`${HEADER_BTN_CLASS} px-2 py-1 rounded text-sm font-bold`}>
              ◀
            </button>

            <span className={`font-semibold text-sm ${HEADER_TEXT_CLASS}`}>
              {new Date(year, month).toLocaleString("default", { month: "long" })} {year}
            </span>

            <button onClick={goToNextMonth} className={`${HEADER_BTN_CLASS} px-2 py-1 rounded text-sm font-bold`}>
              ▶
            </button>
          </div>
        </div>

        {/* Week Days */}
        <div className="grid grid-cols-7 gap-1 text-center text-xs font-bold">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className={`border ${BORDER_COLOR_CLASS} rounded-xl py-1 bg-gray-100`}>
              {d}
            </div>
          ))}
        </div>

      {/* Calendar */}
        <div className="grid grid-cols-7 gap-1 mt-1">
          {calendarDays.map((item, index) => (
            <div
              key={index}
              className={`relative h-16 sm:h-20 border ${BORDER_COLOR_CLASS} rounded-xl p-2 text-sm ${
                item?.isToday ? TODAY_BG_CLASS : ""
              }`}
            >
              <span className={`font-bold ${DATE_FONT_CLASS}`}>{item?.day}</span>

              {item?.leftColor && (
                <span className={`absolute bottom-1.5 left-1.5 w-3 h-3 rounded-full ${item.leftColor}`} />
              )}

              {item && !item.isFutureDate && !item.isHoliday && (
                <span className={`absolute bottom-1.5 right-1.5 w-3 h-3 rounded-full ${item.statusColor}`} />
              )}
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 justify-center mt-4 text-xs">
          <div className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded-full" /> Holiday</div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 bg-sky-400 rounded-full" /> Approved Leave</div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-200 rounded-full" /> Rejected Leave</div>
          <div className="flex items-center gap-1">|</div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded-full" /> Present</div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 bg-black rounded-full" /> Week Off</div>
          <div className="flex items-center gap-1"><span className="w-3 h-3 bg-[rgb(255,128,128)] rounded-full" /> Absent</div>
        </div>
      </div>
    </>
  );
};

export default EmployeeCalendar;
